/**
 * Ce qu'on a décidé avant, dans des cas qui se comparent (V1.6-15.3).
 *
 * Le premier usage visible de la boucle d'apprentissage, et il **ne recommande
 * rien**. Ce que cet écran tient, et qui est tout le ticket :
 *
 * * **il parle comme un historique, pas comme une prédiction.** « Alpha retenu
 *   3 fois sur 5 décisions non contraintes » est un fait ; « vous préférez
 *   Alpha à 60 % » serait une inférence, et elle n'a pas sa place ici ;
 * * **libre et contraint ne se mélangent jamais.** Une décision prise faute
 *   d'alternative ne dit rien d'un goût, et la fondre dans un total en ferait
 *   une préférence ;
 * * **la taille d'échantillon est toujours là.** Une seule décision s'affiche
 *   comme une seule décision — pas de seuil caché, pas de fausse
 *   significativité ;
 * * **tout se vérifie.** Chaque ligne ouvre le plan réel, et dit pourquoi elle
 *   est dans l'échantillon.
 *
 * Le nom d'un fournisseur affiché ici est son nom **d'aujourd'hui** : ce qui est
 * historique est l'identifiant. L'écran le dit plutôt que de laisser croire
 * qu'on lit une étiquette d'époque.
 */
import { Link } from "react-router-dom";
import { History } from "lucide-react";

import { useComparableDecisions } from "@/api/learning";
import type { ComparableDecisionHistory, DecisionSummary } from "@/api/learning";
import type { PurchasePlanDetail } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";

/** Douze mois : au-delà, fournisseurs et prix ont souvent trop changé. */
const WINDOW_DAYS = 365;

export function ComparableDecisionsCard({ detail }: { detail: PurchasePlanDetail }) {
  const productId = detail.lines.find((line) => line.product_id)?.product_id ?? undefined;
  const { data, isLoading, isError } = useComparableDecisions({
    productId: productId ?? undefined,
    level: "SAME_PRODUCT",
    currentDecisionRef: detail.plan.id,
    periodDays: WINDOW_DAYS,
    limit: 10,
  });

  return (
    <Card>
      <CardHeader title="Décisions comparables" subtitle="Sur le même produit" />
      <CardBody className="space-y-3">
        {/* La mise en garde vit dans le corps, pas dans le sous-titre : à
            390 px l'en-tête tronque, et c'est précisément cette phrase qui
            disparaissait. */}
        <p className="text-[12px] text-ink-subtle">
          Ce qui a été décidé avant. Des faits, pas une recommandation.
        </p>
        {!productId ? (
          <p className="text-[13px] text-ink-subtle">
            Ce plan ne retient aucune ligne rattachée au catalogue : il n'y a rien à comparer.
          </p>
        ) : isLoading ? (
          <p className="text-[13px] text-ink-subtle">Lecture de l'historique…</p>
        ) : isError || !data ? (
          <p className="text-[13px] text-ink-subtle">
            L'historique n'a pas pu être lu. Rien n'est supposé à la place.
          </p>
        ) : (
          <HistoryBody history={data} />
        )}
      </CardBody>
    </Card>
  );
}

function HistoryBody({ history }: { history: ComparableDecisionHistory }) {
  if (history.sample_size === 0) {
    return (
      <p className="text-[13px] text-ink-subtle">
        Aucune décision comparable sur les {WINDOW_DAYS} derniers jours.
      </p>
    );
  }

  return (
    <>
      <Sample history={history} />
      <Suppliers history={history} />
      <Outcomes history={history} />
      <Decisions decisions={history.decisions} evaluated={history.evaluated_sample_count} />
      <p className="text-[12px] text-ink-subtle">
        Les noms de fournisseurs affichés sont les noms courants.
      </p>
    </>
  );
}

/**
 * La phrase qui situe l'échantillon.
 *
 * Elle vient en premier parce que les comptes qui suivent n'ont aucun sens sans
 * elle : trois décisions sur douze mois et trois décisions sur cinq ans ne se
 * lisent pas de la même façon.
 */
function Sample({ history }: { history: ComparableDecisionHistory }) {
  return (
    <div className="space-y-1">
      <p className="text-[13px] text-ink">
        <strong>
          {history.sample_size} décision{history.sample_size > 1 ? "s" : ""} comparable
          {history.sample_size > 1 ? "s" : ""}
        </strong>{" "}
        entre le {formatDate(history.period_start)} et le {formatDate(history.period_end)}.
      </p>
      <p className="text-[12px] text-ink-subtle">
        {history.free_sample_size} non contrainte{history.free_sample_size > 1 ? "s" : ""},{" "}
        {history.constrained_sample_size} contrainte
        {history.constrained_sample_size > 1 ? "s" : ""}
        {history.partial_sample_size > 0
          ? ` · ${history.partial_sample_size} avec des données historiques incomplètes`
          : ""}
        {history.pending_outcome_count > 0
          ? ` · ${history.pending_outcome_count} résultat${
              history.pending_outcome_count > 1 ? "s" : ""
            } encore en attente`
          : ""}
        .
      </p>
      {history.truncated ? (
        <p className="text-[12px] text-ink-subtle">
          Les comptes ci-dessous portent sur {history.evaluated_sample_count} de ces{" "}
          {history.sample_size} décisions — les plus récentes. Affiner la période réduit
          l'échantillon et le rend complet.
        </p>
      ) : null}
      {history.sample_size === 1 ? (
        <p className="text-[12px] text-ink-subtle">
          Une seule décision : trop peu pour y voir une tendance.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Qui a été retenu, et combien de fois.
 *
 * Les deux colonnes ne se cumulent pas en un pourcentage : une décision
 * contrainte n'est pas un choix, et l'additionner aux autres ferait passer une
 * absence d'alternative pour une préférence.
 */
function Suppliers({ history }: { history: ComparableDecisionHistory }) {
  if (history.suppliers.length === 0) return null;

  return (
    <div className="space-y-1">
      <h3 className="text-[13px] font-medium text-ink">Fournisseurs retenus</h3>

      {/* Sous `sm`, une table de trois colonnes se tasse au point que le nom
          touche les chiffres. On empile — c'est la règle de la maison : les
          cartes servent le mobile, les tables l'administration. */}
      <ul className="space-y-1 sm:hidden">
        {history.suppliers.map((supplier) => (
          <li key={supplier.supplier_id} className="border-t border-line pt-1">
            <p className="text-[13px] text-ink">{supplier.supplier_name}</p>
            <p className="text-[12px] text-ink-subtle">
              <span className="tabular-nums text-ink">{supplier.free_decisions}</span> décision
              {supplier.free_decisions > 1 ? "s" : ""} libre
              {supplier.free_decisions > 1 ? "s" : ""} ·{" "}
              <span className="tabular-nums text-ink">{supplier.constrained_decisions}</span>{" "}
              contrainte{supplier.constrained_decisions > 1 ? "s" : ""}
            </p>
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto sm:block">
        {/* Bornée : sur un large écran, des colonnes alignées à droite sur
            onze cents pixels séparent le nom de son chiffre au point qu'on
            ne sait plus les associer. */}
        <table className="w-full max-w-xl text-[13px]">
          <thead>
            <tr className="text-left text-ink-subtle">
              <th className="py-1 pr-4 font-medium">Fournisseur</th>
              <th className="whitespace-nowrap py-1 pr-4 text-right font-medium">
                Décisions libres
              </th>
              <th className="whitespace-nowrap py-1 text-right font-medium">
                Décisions contraintes
              </th>
            </tr>
          </thead>
          <tbody>
            {history.suppliers.map((supplier) => (
              <tr key={supplier.supplier_id} className="border-t border-line">
                <td className="py-1 pr-4">{supplier.supplier_name}</td>
                <td className="py-1 pr-4 text-right tabular-nums">{supplier.free_decisions}</td>
                <td className="py-1 text-right tabular-nums">{supplier.constrained_decisions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[12px] text-ink-subtle">
        « Libres » : le choix était possible. « Contraintes » : il ne l'était pas. Une décision
        peut retenir plusieurs fournisseurs, donc ces nombres ne totalisent pas l'échantillon.
      </p>
    </div>
  );
}

/**
 * Ce qui est arrivé ensuite.
 *
 * Séparé des choix, et nommé comme un fait : un fournisseur qui a décliné reste
 * un fournisseur qui avait été retenu.
 */
function Outcomes({ history }: { history: ComparableDecisionHistory }) {
  const counts = history.outcomes;
  const rows: Array<[string, number]> = [
    ["Confirmées par le fournisseur", counts.supplier_confirmed],
    ["Refusées par le fournisseur", counts.supplier_declined],
    ["Réceptions complètes", counts.receipt_complete],
    ["Reliquats abandonnés", counts.abandoned],
    ["Retours fournisseur", counts.supplier_return],
  ];
  const shown = rows.filter(([, value]) => value > 0);

  if (shown.length === 0) return null;

  return (
    <div className="space-y-1">
      <h3 className="text-[13px] font-medium text-ink">Ce qui a suivi</h3>
      <ul className="space-y-0.5 text-[13px] text-ink-subtle">
        {shown.map(([label, value]) => (
          <li key={label}>
            {label} : <span className="tabular-nums text-ink">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Chaque décision, ouvrable. Personne ne doit croire sur parole. */
function Decisions({
  decisions,
  evaluated,
}: {
  decisions: DecisionSummary[];
  /** Ce que le résumé a relu — **pas** la taille du filtre. Dire « sur 501 »
      quand cinq cents ont été lues serait juste dans ses chiffres et faux dans
      sa portée. */
  evaluated: number;
}) {
  if (decisions.length === 0) return null;

  return (
    <div className="space-y-1">
      <h3 className="text-[13px] font-medium text-ink">Les décisions</h3>
      <ul className="space-y-2">
        {decisions.map((decision) => (
          <li key={decision.decision_ref} className="border-t border-line pt-2">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/purchase-plans/${decision.decision_ref}`}
                className="text-[13px] font-medium text-accent hover:underline"
              >
                {decision.decision_group_ref} · rév. {decision.revision}
              </Link>
              {decision.decision_at ? (
                <span className="text-[12px] text-ink-subtle">
                  {formatDate(decision.decision_at)}
                </span>
              ) : null}
              {decision.constrained ? (
                <StatusBadge tone="warning">Contrainte</StatusBadge>
              ) : null}
              {decision.outcome_pending ? (
                <StatusBadge tone="unknown">Résultat en attente</StatusBadge>
              ) : null}
              {decision.completeness !== "COMPLETE" ? (
                <StatusBadge tone="unknown">Données partielles</StatusBadge>
              ) : null}
            </div>
            <p className="mt-0.5 text-[12px] text-ink-subtle">
              {decision.supplier_names.filter(Boolean).join(", ") || "Fournisseur inconnu"}
              {decision.actor_role ? ` · signée par un rôle ${decision.actor_role}` : ""}
            </p>
            <p className="mt-0.5 flex items-center gap-1 text-[12px] text-ink-subtle">
              <History aria-hidden className="size-3" />
              {decision.comparison_reasons.map(reasonLabel).join(" · ")}
            </p>
          </li>
        ))}
      </ul>
      {evaluated > decisions.length ? (
        <p className="text-[12px] text-ink-subtle">
          {decisions.length} décision{decisions.length > 1 ? "s" : ""} affichée
          {decisions.length > 1 ? "s" : ""} sur les {evaluated} résumées.
        </p>
      ) : null}
    </div>
  );
}

/** Pourquoi cette décision est dans l'échantillon, en français. */
function reasonLabel(reason: string): string {
  switch (reason) {
    case "SAME_PRODUCT":
      return "Même produit";
    case "VERIFIED_EQUIVALENT":
      // Jamais « même famille » : une équivalence affirmée ne dit rien d'une
      // lignée, et le catalogue n'en porte aucune.
      return "Produit relié par une équivalence vérifiée";
    case "SAME_CATEGORY":
      return "Même catégorie";
    case "SAME_PROJECT":
      return "Même chantier";
    case "COMPARABLE_UNIT":
      return "Unité comparable";
    case "SAME_TRACKING_MODE":
      return "Même mode de suivi";
    default:
      return reason;
  }
}
